import { useEffect, useRef, useCallback } from 'react';

export const useWakeLock = () => {
  const wakeLockRef = useRef(null);

  const requestWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('✅ Wake Lock activated - screen will stay on');
        
        wakeLockRef.current.addEventListener('release', () => {
          console.log('⚠️ Wake Lock released');
        });
      } catch (err) {
        console.log('Wake Lock error:', err.message);
      }
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
      console.log('Wake Lock manually released');
    }
  }, []);

  // Re-acquire wake lock when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      releaseWakeLock();
    };
  }, [requestWakeLock, releaseWakeLock]);

  return { requestWakeLock, releaseWakeLock };
};

export const useAudioKeepAlive = () => {
  const silentAudioRef = useRef(null);
  const audioContextRef = useRef(null);

  const startKeepAlive = useCallback(() => {
    // Create a silent audio context to keep audio system active
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContext();
      
      // Create silent oscillator
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      // Set volume to 0 (silent)
      gainNode.gain.value = 0;
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      oscillator.start();
      
      console.log('✅ Silent audio keep-alive started');
    } catch (err) {
      console.log('Audio keep-alive error:', err.message);
    }
  }, []);

  const stopKeepAlive = useCallback(() => {
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  // Handle visibility change - resume audio when coming back
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // Resume audio context if suspended
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          try {
            await audioContextRef.current.resume();
            console.log('✅ Audio context resumed');
          } catch (err) {
            console.log('Failed to resume audio:', err);
          }
        }
        
        // Also try to resume all audio elements on the page
        document.querySelectorAll('audio').forEach(async (audio) => {
          try {
            if (audio.paused && audio.srcObject) {
              await audio.play();
            }
          } catch (err) {
            console.log('Failed to resume audio element:', err);
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopKeepAlive();
    };
  }, [stopKeepAlive]);

  return { startKeepAlive, stopKeepAlive };
};